import { type ColumnOption } from "interfaces/columnOptions";
import { Tooltip } from "components/common/tooltip";
import Title from "components/listViewTemplates/title/Title";
import PublishStatus from "components/common/PublishStatus/PublishStatus";
import { type PriorityLevels, PublishedStatusTypes } from "enums";
import { ObjectiveLabel } from "features/Library/Flows/common/ObjectiveLabel/ObjectiveLabel";
import PriorityCell from "components/priorityLevels/priorityCell/PriorityCell";
import { ContentType, type PeopleType } from "features/Library/PeopleAssignments/types";
import EllipsisPopupButton from "components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import { HiddenContent } from "components/HiddenContent/HiddenContent";
import {
  type ContentAssignmentModelItem,
  ContentColumns,
  type GroupScheduleResponse,
  type IFlowAssignmentModelItem,
} from "features/People/types";
import { TextTruncate } from "components/textTruncators/TextTruncators";
import EditPriorityLinkButton from "components/buttons/linkButtons/editPriorityLinkButton/EditPriorityLinkButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import { ScheduleStatus } from "./schedules/ScheduleStatus";
import { ScheduleTitle } from "./schedules/ScheduleTitle";

interface GetFlowColumnOptionsParams {
  url: string;
  selectedIds: number[];
  contextMenuButtonHandlers: {
    onEditPriority: (item: IFlowAssignmentModelItem) => void;
    onClearPriority: (ids: number[]) => void;
  };
  peopleType: PeopleType;
  disabled: boolean;
}

/* istanbul ignore next */
export const getFlowColumnOptions = (props: GetFlowColumnOptionsParams): ColumnOption<IFlowAssignmentModelItem>[] => {
  return [
    {
      name: ContentColumns.Title,
      width: 5,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden} showIcon>
          <Title className="normal-color" title={item.title} url={`${props.url}/${item.id}`} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Objective,
      width: 3,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <ObjectiveLabel objective={item.goals?.objective} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={item.hidden}>
                {item.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={item.publisher}
        />
      ),
    },
    {
      name: ContentColumns.Status,
      width: 2,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <PublishStatus publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(item.published)} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.PriorityLevel,
      width: 3,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={item.hidden}>
          <PriorityCell
            peopleType={props.peopleType}
            contentType={ContentType.Flow}
            priorityLevel={item.priorityLevelId as PriorityLevels}
            dueDate={item.dueDate}
            daysToComplete={item.daysToComplete}
          />
        </HiddenContent>
      ),
    },
    {
      name: "",
      width: 1,
      render: (item) => (
        <EllipsisPopupButton
          circle
          outlinedEllipsis
          disabled={item.isInherited || props.selectedIds.includes(item.id) || props.disabled}
        >
          <EditPriorityLinkButton onClick={() => props.contextMenuButtonHandlers.onEditPriority(item)} />
          <RemovePriorityButton
            key="remove-priority-menu-button"
            onClick={() => props.contextMenuButtonHandlers.onClearPriority([item.id])}
            isDisabled={!item.canRemove}
            context="people"
            entityType={ContentType.Flow}
            peopleType={props.peopleType}
            showTooltip={!item.canRemove}
            multiple={false}
          />
        </EllipsisPopupButton>
      ),
      className: "align-right",
    },
  ];
};

export const getScheduleColumnOptions = (props: GetFlowColumnOptionsParams): ColumnOption<GroupScheduleResponse>[] => {
  return [
    {
      name: ContentColumns.Title,
      width: 5,
      isSortable: true,
      render: (item) => (
        <ScheduleTitle
          title={item.title}
          hasConsumableItems={item.hasConsumableItems}
          canAutoStart={item.canAutoStart}
        />
      ),
    },
    {
      name: ContentColumns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => <Tooltip target={<TextTruncate>{item.publisher}</TextTruncate>} content={item.publisher} />,
    },
    {
      name: ContentColumns.PriorityLevel,
      width: 3,
      isSortable: true,
      render: (item) => (
        <PriorityCell
          peopleType={props.peopleType}
          contentType={ContentType.Flow}
          priorityLevel={item.priority as PriorityLevels}
          dueDate={item.dueDate}
          daysToComplete={item.daysToComplete}
        />
      ),
    },
    {
      name: ContentColumns.ScheduledFor,
      width: 3,
      isSortable: true,
      render: (item) => (
        <ScheduleStatus status={item.status} startDate={item.startFlowDate} endDate={item.endFlowDate} />
      ),
    },
    {
      name: ContentColumns.Trigger,
      width: 2,
      isSortable: false,
      render: (item) => {
        return !item.hasTriggers ? (
          "-"
        ) : (
          <Tooltip
            target={<FontAwesomeIcon icon={faBolt} aria-label="Trigger enabled" size="lg" color="gray" />}
            content="This schedule uses external triggers."
          />
        );
      },
      className: "align-center",
    },
  ];
};

/* istanbul ignore next */
export const getColumnOptions = (url: string): ColumnOption<ContentAssignmentModelItem>[] => {
  return [
    {
      name: ContentColumns.Title,
      width: 4,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility} showIcon>
          <Title className="normal-color" title={item.title} url={`${url}/${item.id}`} />
        </HiddenContent>
      ),
    },
    {
      name: ContentColumns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={!item.visibility}>
                {item.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={item.publisher}
        />
      ),
    },
    {
      name: ContentColumns.Status,
      width: 6,
      isSortable: false,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          <PublishStatus publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!item.isDraft)} />
        </HiddenContent>
      ),
    },
  ];
};
